import store from '@/store';
import router from '@/router';
import { convertToFormData } from '@/scripts/tools.js';
import { saveToken, getToken, deleteToken } from '@/scripts/token.js';
import constants from '@/constants';
import { axiosRequest, METHOD } from '@/api/default.js';

function getLoginErrorText(error) {
    // 429 – progressive delay (flat body, no detail wrapper)
    if (error.response?.status === 429) {
        const data = error.response.data;
        const seconds = data?.retry_after_seconds;
        return seconds
            ? `Too many failed login attempts. Please wait ${seconds} second${seconds !== 1 ? 's' : ''} before trying again.`
            : (data?.message || 'Too many failed login attempts. Please wait before retrying.');
    }

    // Early return for no response
    if (!error.response?.data?.detail) {
        return 'Server is not available, please try again later';
    }

    const detail = error.response.data.detail;

    // Early return for string details
    if (typeof detail !== 'object') {
        return detail;
    }

    // Early return for CAPTCHA error
    if (detail.error === 'CAPTCHA_REQUIRED') {
        return 'CAPTCHA verification required';
    }

    // Default for object details
    return detail.message || detail.error || 'Login failed';
}

export async function userLogin(
    login,
    password,
    captchaId = null,
    captchaInput = null
) {
    // Validate required parameters
    if (!login || !password) {
        return {
            authorization: false,
            response: null,
            errorText: 'Username and password are required',
        };
    }

    const formData = {
        username: login.trim(),
        password: password,
    };

    // Add CAPTCHA data if both are provided
    if (captchaId && captchaInput && captchaInput.trim()) {
        formData.challenge_id = captchaId;
        formData.captcha_user_input = captchaInput.trim();
    }

    const userData = await convertToFormData(formData);

    try {
        const data = await axiosRequest({
            url: constants.URL.AUTH_LOGIN,
            method: METHOD.POST,
            requestName: 'LOGIN',
            data: userData,
            isFormData: true,
            skipAuth: true,
            skipErrorPopup: true,
            isLoader: false,
            throwOnError: true,
        });

        await saveToken(data);

        return {
            authorization: true,
            response: { data },
        };
    } catch (error) {
        // 429 – progressive delay
        if (error.response?.status === 429) {
            const data = error.response.data;
            const errorText = getLoginErrorText(error);
            return {
                authorization: false,
                response: error.response,
                errorText,
                rateLimited: true,
                retryAfterSeconds: data?.retry_after_seconds ?? 0,
            };
        }

        const errorText = getLoginErrorText(error);

        return {
            authorization: false,
            response: error.response,
            errorText: errorText,
        };
    }
}

// Use Axios's built-in cancellation for refreshUser
let refreshController = null;

export async function refreshUser() {
    // If there's already a refresh in progress, return the existing promise
    if (refreshController && !refreshController.signal.aborted) {
        // Wait for the existing request to complete
        try {
            return await refreshController.promise;
        } catch (error) {
            // If the existing request failed, allow this call to proceed
        }
    }

    // Create new AbortController for this refresh attempt
    refreshController = new AbortController();

    // Store the promise on the controller for reuse
    refreshController.promise = (async () => {
        try {
            const data = await axiosRequest({
                url: constants.URL.AUTH_REFRESH,
                method: METHOD.GET,
                requestName: 'REFRESH TOKEN',
                customHeaders: {
                    'refresh-token': localStorage.refresh_token,
                },
                signal: refreshController.signal,
                skipAuth: true,
                skipErrorPopup: true,
                isLoader: false,
                throwOnError: true,
            });

            if (data) {
                saveToken(data);
            }
            return data;
        } catch (error) {
            // Don't handle aborted requests (they were cancelled intentionally)
            if (error.name === 'CanceledError') {
                throw error;
            }

            deleteToken();
            store.dispatch('LOGOUT_USER');
            router.push({ name: constants.ROUTER_NAME.AUTHORIZATION });
            throw error;
        } finally {
            // Clear the controller after completion
            refreshController = null;
        }
    })();

    return refreshController.promise;
}

export async function getUsers(params = {}) {
    const url = constants.URL.USER_GET;
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName: 'GET USERS',
        params,
        isLoader: false,
    });

    // Return full paginated response structure
    return response == null
        ? {
              data: [],
              pagination: { total: 0, limit: 100, offset: 0, count: 0 },
              links: {},
          }
        : response;
}

export async function getUsersAxios(params = {}) {
    // Fallback for non-paginated use cases - fetch first 500
    const response = await getUsers({ ...params, limit: 500, offset: 0 });
    return response.data;
}

export async function getRolesAxios() {
    const token = await getToken();
    if (token === null) {
        throw 'Authorization token was not detected';
    }

    return {
        method: METHOD.GET,
        url: `${process.env.VUE_APP_BACKEND_URL}${constants.URL.ROLE_GET}`,
        headers: {
            Authorization: token,
            'Cache-Control': 'no-cache',
        },
    };
}

export async function getRoles() {
    const response = await axiosRequest({
        url: constants.URL.ROLE_GET,
        method: METHOD.GET,
        requestName: 'GET ROLE',
        isLoader: false,
    });

    return response == null ? [] : response;
}

export async function changeUserStatus(userID, status = true) {
    const response = await axiosRequest({
        url: constants.URL.USER_ACTIVE,
        method: METHOD.POST,
        requestName: 'CHANGE USER STATE',
        params: {
            user_id: userID,
            activate: status,
        },
    });

    return response == null ? false : true;
}

export async function saveUser(id, name, company) {
    const response = await axiosRequest({
        url: constants.URL.USER_UPDATE,
        method: METHOD.PATCH,
        requestName: 'SAVE USER',
        data: {
            id,
            name,
            company,
        },
    });

    return response !== null;
}

export async function saveRole(data) {
    const response = await axiosRequest({
        url: constants.URL.ROLE_UPDATE,
        method: METHOD.PATCH,
        requestName: 'SAVE ROLE',
        data: data,
    });

    return response == null ? false : true;
}

export async function assignRole(userID, roleID) {
    const response = await axiosRequest({
        url: constants.URL.ROLE_ASSIGN,
        method: METHOD.PATCH,
        requestName: 'ASSIGN ROLE',
        params: {
            role_id: roleID,
            user_id: userID,
        },
    });

    return response == null ? false : true;
}

export async function registerUser({ name, company, email, password }) {
    const userData = await convertToFormData({
        name: name,
        password: password,
        company: company,
        email: email,
    });

    const response = await axiosRequest({
        url: constants.URL.AUTH_REGISTER,
        method: METHOD.POST,
        requestName: 'REGISTER USER',
        data: userData,
        isFormData: true,
    });

    return response;
}

export async function addRole(data) {
    const response = await axiosRequest({
        url: constants.URL.ROLE_CREATE,
        method: METHOD.POST,
        requestName: 'CREATE ROLE',
        data: data,
    });

    return response;
}

export async function deleteRole(id) {
    const response = await axiosRequest({
        url: constants.URL.ROLE_DELETE,
        method: METHOD.DELETE,
        requestName: 'DELETE ROLE',
        params: {
            user_role_id: id,
        },
    });

    return response == null ? false : true;
}

export async function changeUserPassword(userId, newPassword) {
    if (!userId || !newPassword) {
        throw new Error('User ID and new password are required');
    }

    const url = constants.URL.USER_CHANGE_PASSWORD.replace('{user_id}', userId);

    const response = await axiosRequest({
        url: url,
        method: METHOD.POST,
        requestName: 'CHANGE USER PASSWORD',
        data: {
            new_password: newPassword,
        },
    });

    return response != null;
}

export async function getUserSessions(userId) {
    if (!userId) {
        throw new Error('User ID is required');
    }

    const url = constants.URL.USER_SESSIONS_GET.replace('{user_id}', userId);

    const response = await axiosRequest({
        url: url,
        method: METHOD.GET,
        requestName: 'GET USER SESSIONS',
        isLoader: false,
    });

    return response == null ? [] : response;
}

export async function deleteUserSession(userId, sessionId) {
    if (!userId || !sessionId) {
        throw new Error('User ID and Session ID are required');
    }

    const url = constants.URL.USER_SESSION_DELETE.replace(
        '{user_id}',
        userId
    ).replace('{session_id}', sessionId);

    const response = await axiosRequest({
        url: url,
        method: METHOD.DELETE,
        requestName: 'DELETE USER SESSION',
    });

    return response != null;
}

export async function getUserDetails(userId) {
    if (!userId) {
        throw new Error('User ID is required');
    }

    const url = constants.URL.USER_DETAILS.replace('{user_id}', userId);

    const response = await axiosRequest({
        url: url,
        method: METHOD.GET,
        requestName: 'GET USER DETAILS',
        isLoader: false,
        isShowPopup: false,
    });

    return response || null;
}

export async function resetUserSubscriptionData(userId) {
    if (!userId) return null;

    const url = constants.URL.ADMIN_USER_RESET_SUBSCRIPTION.replace(
        '{user_id}',
        userId
    );

    return await axiosRequest({
        url: url,
        method: METHOD.POST,
        requestName: 'RESET USER SUBSCRIPTION DATA',
        isLoader: true,
        isShowPopup: false,
    });
}
